import type { Locale } from '@spot/shared';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { formatDate } from '../events/format';
import { DEFAULT_CITY } from '../map/city';
import { usePersonName } from '../profile/person-link';
import { useUserReviews } from './use-reviews';

/** Profile «Відгуки» (S12/S13). The API sends nothing below 3 reviews (D13): then it hides. */
export function ReviewsSection({ userId, locale }: { userId: string; locale: Locale }) {
	const { t } = useTranslation();
	const nameOf = usePersonName();
	const query = useUserReviews(userId);
	const items = query.data?.pages.flatMap((page) => page.items) ?? [];
	if (items.length === 0) return null;

	return (
		<section>
			<h2 className="text-body text-text mb-2 font-semibold">{t('reviews.section')}</h2>
			<ul className="flex flex-col gap-3">
				{items.map((review) => (
					<li key={review.id} className="bg-surface border-border rounded-card border p-3">
						<div className="flex items-center gap-3">
							<Avatar
								name={review.author.displayName}
								photoUrl={review.author.photoUrl}
								size="size-8"
							/>
							<div className="min-w-0 flex-1">
								<p className="text-body text-text truncate font-semibold">
									{nameOf(review.author.displayName)}
								</p>
								<p className="text-caption text-text-muted truncate">
									{review.event.title} ·{' '}
									{formatDate(new Date(review.createdAt), DEFAULT_CITY.timezone, locale)}
								</p>
							</div>
							<span
								className="text-body text-primary"
								aria-label={t('reviews.stars', { count: review.rating })}
							>
								{'★'.repeat(review.rating)}
							</span>
						</div>
						{review.comment !== null && (
							<p className="text-body text-text mt-2 whitespace-pre-line">{review.comment}</p>
						)}
					</li>
				))}
			</ul>
			{query.hasNextPage && (
				<Button
					variant="ghost"
					className="mt-2 w-full"
					disabled={query.isFetchingNextPage}
					onClick={() => void query.fetchNextPage()}
				>
					{t('reviews.more')}
				</Button>
			)}
		</section>
	);
}
